/**
 * Organizations Tab for Admin Client Detail Page
 * Full CRUD for organizations: create, edit (inline expand), deactivate (soft-delete).
 * Includes per-org user membership management.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Building02, ChevronDown, Plus, Trash01, XClose } from "@untitledui/icons";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { Select, type SelectItemType } from "@/components/base/select/select";
import { SelectItem } from "@/components/base/select/select-item";
import { Toggle } from "@/components/base/toggle/toggle";
import { useAdminEnums } from "@/hooks/use-admin-enums";
import { useAdminApi } from "@/services/api/admin-api";
import type { CreateOrganizationRequestDto, OrganizationAdminListItemDto, UpdateOrganizationRequestDto, UserAdminListItemDto } from "@/types/api/admin";

// =============================================================================
// TYPES
// =============================================================================

interface OrganizationsTabProps {
    clientId: number;
}

// =============================================================================
// CREATE FORM PANEL (shown above table when creating a new org)
// =============================================================================

interface OrgCreatePanelProps {
    clientId: number;
    typeOptions: SelectItemType[];
    onSaved: () => void;
    onCancel: () => void;
}

function OrgCreatePanel({ clientId, typeOptions, onSaved, onCancel }: OrgCreatePanelProps) {
    const adminApi = useAdminApi();

    const [code, setCode] = useState("");
    const [name, setName] = useState("");
    const [type, setType] = useState<string>(typeOptions[0]?.id ?? "Store");
    const [isActive, setIsActive] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleSubmit = useCallback(async () => {
        if (!name.trim()) {
            setError("Name is required.");
            return;
        }
        if (!code.trim()) {
            setError("Code is required.");
            return;
        }

        setIsSaving(true);
        setError(null);
        try {
            const dto: CreateOrganizationRequestDto = { code: code.trim(), name: name.trim(), type, isActive };
            await adminApi.createClientOrganization(clientId, dto);
            onSaved();
        } catch (err) {
            const msg = err instanceof Error ? err.message : "Failed to save organization.";
            setError(
                msg.includes("409") || msg.toLowerCase().includes("conflict")
                    ? `An organization with code "${code.trim()}" already exists for this client.`
                    : msg,
            );
        } finally {
            setIsSaving(false);
        }
    }, [adminApi, clientId, code, isActive, name, onSaved, type]);

    return (
        <div className="rounded-lg border border-secondary bg-primary p-5">
            <h3 className="mb-4 text-sm font-semibold text-primary">Add Organization</h3>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                    <label className="mb-1.5 block text-sm font-medium text-secondary">
                        Code <span className="text-error-primary">*</span>
                    </label>
                    <Input value={code} onChange={setCode} placeholder="e.g. STORE-001" />
                </div>

                <div>
                    <label className="mb-1.5 block text-sm font-medium text-secondary">
                        Name <span className="text-error-primary">*</span>
                    </label>
                    <Input value={name} onChange={setName} placeholder="Organization name" />
                </div>

                <div>
                    <label className="mb-1.5 block text-sm font-medium text-secondary">Type</label>
                    <Select label="Type" items={typeOptions} selectedKey={type} onSelectionChange={(key) => setType(String(key))}>
                        {(item) => <SelectItem id={item.id}>{item.label}</SelectItem>}
                    </Select>
                </div>

                <div className="flex items-center gap-3 pt-6">
                    <Toggle isSelected={isActive} onChange={setIsActive} />
                    <span className="text-sm text-secondary">Active</span>
                </div>
            </div>

            {error && <p className="mt-3 text-sm text-error-primary">{error}</p>}

            <div className="mt-4 flex gap-2">
                <Button color="primary" size="sm" onClick={handleSubmit} isDisabled={isSaving}>
                    {isSaving ? "Saving…" : "Create Organization"}
                </Button>
                <Button color="secondary" size="sm" onClick={onCancel} isDisabled={isSaving}>
                    Cancel
                </Button>
            </div>
        </div>
    );
}

// =============================================================================
// EDIT FORM (inline in expanded row)
// =============================================================================

interface OrgEditFormProps {
    clientId: number;
    org: OrganizationAdminListItemDto;
    typeOptions: SelectItemType[];
    onSaved: () => void;
    onCancel: () => void;
}

function OrgEditForm({ clientId, org, typeOptions, onSaved, onCancel }: OrgEditFormProps) {
    const adminApi = useAdminApi();

    const [name, setName] = useState(org.name);
    const [type, setType] = useState<string>(org.type);
    const [isActive, setIsActive] = useState(org.isActive);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleSubmit = useCallback(async () => {
        if (!name.trim()) {
            setError("Name is required.");
            return;
        }

        setIsSaving(true);
        setError(null);
        try {
            const dto: UpdateOrganizationRequestDto = { name: name.trim(), type, isActive };
            await adminApi.updateClientOrganization(clientId, org.id, dto);
            onSaved();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to save organization.");
        } finally {
            setIsSaving(false);
        }
    }, [adminApi, clientId, isActive, name, onSaved, org.id, type]);

    return (
        <div>
            <h4 className="mb-3 text-sm font-semibold text-primary">Edit Organization</h4>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                    <label className="mb-1.5 block text-xs font-medium text-secondary">Code</label>
                    <p className="rounded-md border border-secondary bg-secondary_subtle px-3 py-2 text-sm text-tertiary">{org.code}</p>
                </div>

                <div>
                    <label className="mb-1.5 block text-xs font-medium text-secondary">
                        Name <span className="text-error-primary">*</span>
                    </label>
                    <Input value={name} onChange={setName} placeholder="Organization name" />
                </div>

                <div>
                    <label className="mb-1.5 block text-xs font-medium text-secondary">Type</label>
                    <Select label="Type" items={typeOptions} selectedKey={type} onSelectionChange={(key) => setType(String(key))}>
                        {(item) => <SelectItem id={item.id}>{item.label}</SelectItem>}
                    </Select>
                </div>

                <div className="flex items-center gap-3 pt-5">
                    <Toggle isSelected={isActive} onChange={setIsActive} />
                    <span className="text-sm text-secondary">Active</span>
                </div>
            </div>

            {error && <p className="mt-2 text-xs text-error-primary">{error}</p>}

            <div className="mt-3 flex gap-2">
                <Button color="primary" size="sm" onClick={handleSubmit} isDisabled={isSaving}>
                    {isSaving ? "Saving…" : "Save Changes"}
                </Button>
                <Button color="secondary" size="sm" onClick={onCancel} isDisabled={isSaving}>
                    Cancel
                </Button>
            </div>
        </div>
    );
}

// =============================================================================
// USER MANAGEMENT PANEL (inline in expanded row)
// =============================================================================

interface OrgUserManagementPanelProps {
    clientId: number;
    orgId: number;
    allUsers: UserAdminListItemDto[];
    isLoadingUsers: boolean;
    onMembersChanged: () => void;
}

function OrgUserManagementPanel({ clientId, orgId, allUsers, isLoadingUsers, onMembersChanged }: OrgUserManagementPanelProps) {
    const adminApi = useAdminApi();
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const members = useMemo(() => allUsers.filter((u) => u.organizations.some((o) => o.organizationId === orgId)), [allUsers, orgId]);

    const nonMembers: SelectItemType[] = useMemo(
        () =>
            allUsers
                .filter((u) => u.isActive && !u.organizations.some((o) => o.organizationId === orgId))
                .map((u) => ({
                    id: String(u.id),
                    label: [u.firstName, u.lastName].filter(Boolean).join(" ") || u.email,
                })),
        [allUsers, orgId],
    );

    const handleAdd = useCallback(
        async (key: string | number | null) => {
            if (!key) return;
            setIsSaving(true);
            setError(null);
            try {
                await adminApi.addUserOrganization(clientId, Number(key), orgId);
                onMembersChanged();
            } catch (err) {
                setError(err instanceof Error ? err.message : "Failed to add user.");
            } finally {
                setIsSaving(false);
            }
        },
        [adminApi, clientId, orgId, onMembersChanged],
    );

    const handleRemove = useCallback(
        async (userId: number) => {
            setIsSaving(true);
            setError(null);
            try {
                await adminApi.removeUserOrganization(clientId, userId, orgId);
                onMembersChanged();
            } catch (err) {
                setError(err instanceof Error ? err.message : "Failed to remove user.");
            } finally {
                setIsSaving(false);
            }
        },
        [adminApi, clientId, orgId, onMembersChanged],
    );

    if (isLoadingUsers) {
        return <LoadingIndicator size="sm" label="Loading users…" />;
    }

    return (
        <div>
            <h4 className="mb-3 text-sm font-semibold text-primary">Members</h4>

            {members.length > 0 ? (
                <div className="mb-3 flex flex-wrap gap-1.5">
                    {members.map((user) => (
                        <span
                            key={user.id}
                            className="inline-flex items-center gap-1 rounded-full border border-secondary bg-primary px-2.5 py-1 text-xs font-medium text-primary"
                        >
                            {[user.firstName, user.lastName].filter(Boolean).join(" ") || user.email}
                            <button
                                type="button"
                                className="cursor-pointer rounded-full p-0.5 text-quaternary transition-colors hover:bg-error-secondary hover:text-error-primary"
                                onClick={() => handleRemove(user.id)}
                                disabled={isSaving}
                                title={`Remove ${user.email}`}
                            >
                                <XClose className="size-3" />
                            </button>
                        </span>
                    ))}
                </div>
            ) : (
                <p className="mb-3 text-xs text-quaternary">No members assigned.</p>
            )}

            {nonMembers.length > 0 && (
                <div className="max-w-xs">
                    <Select
                        label="Add member"
                        items={nonMembers}
                        selectedKey={null}
                        onSelectionChange={handleAdd}
                        placeholder="Select user..."
                        size="sm"
                        isDisabled={isSaving}
                    >
                        {(item) => <SelectItem id={item.id}>{item.label}</SelectItem>}
                    </Select>
                </div>
            )}

            {nonMembers.length === 0 && members.length > 0 && <p className="text-xs text-quaternary">All active users are already members.</p>}

            {error && <p className="mt-2 text-xs text-error-primary">{error}</p>}
            {isSaving && <LoadingIndicator size="sm" />}
        </div>
    );
}

// =============================================================================
// DEACTIVATION CONFIRMATION
// =============================================================================

interface DeactivateConfirmProps {
    org: OrganizationAdminListItemDto;
    memberCount: number;
    onConfirm: () => void;
    onCancel: () => void;
    isProcessing: boolean;
}

function DeactivateConfirm({ org, memberCount, onConfirm, onCancel, isProcessing }: DeactivateConfirmProps) {
    return (
        <div className="border-error-secondary bg-error-primary_subtle rounded-lg border p-4">
            <p className="text-sm text-primary">
                <span className="font-semibold">{org.name}</span> has {memberCount} member{memberCount !== 1 ? "s" : ""}. Deactivating will remove them from
                this organization. Continue?
            </p>
            <div className="mt-3 flex gap-2">
                <Button color="primary-destructive" size="sm" onClick={onConfirm} isDisabled={isProcessing}>
                    {isProcessing ? "Deactivating…" : "Deactivate"}
                </Button>
                <Button color="secondary" size="sm" onClick={onCancel} isDisabled={isProcessing}>
                    Cancel
                </Button>
            </div>
        </div>
    );
}

// =============================================================================
// EXPANDED ROW CONTENT
// =============================================================================

interface ExpandedOrgPanelProps {
    clientId: number;
    org: OrganizationAdminListItemDto;
    typeOptions: SelectItemType[];
    allUsers: UserAdminListItemDto[];
    isLoadingUsers: boolean;
    onOrgUpdated: () => void;
    onCollapse: () => void;
    onMembersChanged: () => void;
}

function ExpandedOrgPanel({ clientId, org, typeOptions, allUsers, isLoadingUsers, onOrgUpdated, onCollapse, onMembersChanged }: ExpandedOrgPanelProps) {
    const adminApi = useAdminApi();

    // Deactivation state
    const [pendingMemberCount, setPendingMemberCount] = useState(0);
    const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);
    const [isDeactivating, setIsDeactivating] = useState(false);
    const [deactivateError, setDeactivateError] = useState<string | null>(null);

    const handleDeactivateClick = useCallback(async () => {
        setDeactivateError(null);
        try {
            const preCheck = await adminApi.deactivateClientOrganization(clientId, org.id, false);
            if (preCheck.userCount === 0) {
                setIsDeactivating(true);
                await adminApi.deactivateClientOrganization(clientId, org.id, true);
                setIsDeactivating(false);
                onOrgUpdated();
            } else {
                setPendingMemberCount(preCheck.userCount);
                setShowDeactivateConfirm(true);
            }
        } catch (err) {
            setDeactivateError(err instanceof Error ? err.message : "Failed to deactivate.");
            setIsDeactivating(false);
        }
    }, [adminApi, clientId, org.id, onOrgUpdated]);

    const handleDeactivateConfirm = useCallback(async () => {
        setIsDeactivating(true);
        setDeactivateError(null);
        try {
            await adminApi.deactivateClientOrganization(clientId, org.id, true);
            setShowDeactivateConfirm(false);
            onOrgUpdated();
        } catch (err) {
            setDeactivateError(err instanceof Error ? err.message : "Failed to deactivate.");
        } finally {
            setIsDeactivating(false);
        }
    }, [adminApi, clientId, org.id, onOrgUpdated]);

    return (
        <div className="flex flex-col gap-5 border-t border-secondary bg-secondary_subtle px-6 py-5">
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                {/* Edit form */}
                <OrgEditForm
                    clientId={clientId}
                    org={org}
                    typeOptions={typeOptions}
                    onSaved={() => {
                        onOrgUpdated();
                        onCollapse();
                    }}
                    onCancel={onCollapse}
                />

                {/* User management */}
                <OrgUserManagementPanel
                    clientId={clientId}
                    orgId={org.id}
                    allUsers={allUsers}
                    isLoadingUsers={isLoadingUsers}
                    onMembersChanged={onMembersChanged}
                />
            </div>

            {/* Deactivate section */}
            {org.isActive && (
                <div className="border-t border-secondary pt-4">
                    {showDeactivateConfirm ? (
                        <DeactivateConfirm
                            org={org}
                            memberCount={pendingMemberCount}
                            onConfirm={handleDeactivateConfirm}
                            onCancel={() => setShowDeactivateConfirm(false)}
                            isProcessing={isDeactivating}
                        />
                    ) : (
                        <div className="flex items-center gap-3">
                            <Button color="secondary-destructive" size="sm" iconLeading={Trash01} onClick={handleDeactivateClick} isDisabled={isDeactivating}>
                                {isDeactivating ? "Deactivating…" : "Deactivate Organization"}
                            </Button>
                        </div>
                    )}
                    {deactivateError && <p className="mt-2 text-xs text-error-primary">{deactivateError}</p>}
                </div>
            )}
        </div>
    );
}

// =============================================================================
// ORGANIZATIONS TAB
// =============================================================================

export function OrganizationsTab({ clientId }: OrganizationsTabProps) {
    const adminApi = useAdminApi();
    const { getOptions, isLoaded: enumsLoaded } = useAdminEnums();

    const [orgs, setOrgs] = useState<OrganizationAdminListItemDto[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // All users for this client (loaded once, used by user management panel)
    const [allUsers, setAllUsers] = useState<UserAdminListItemDto[]>([]);
    const [isLoadingUsers, setIsLoadingUsers] = useState(false);

    // Create form
    const [showCreateForm, setShowCreateForm] = useState(false);

    // Row expansion
    const [expandedOrgId, setExpandedOrgId] = useState<number | null>(null);

    const typeOptions: SelectItemType[] = enumsLoaded
        ? getOptions("OrganizationTypeEnum").map((opt) => ({ id: opt.value, label: opt.label }))
        : ["Store", "CompanyCode", "Division", "Department", "Plant"].map((v) => ({ id: v, label: v }));

    const fetchOrgs = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await adminApi.listClientOrganizations(clientId);
            setOrgs(data);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to load organizations.");
        } finally {
            setIsLoading(false);
        }
    }, [adminApi, clientId]);

    const fetchUsers = useCallback(async () => {
        setIsLoadingUsers(true);
        try {
            const res = await adminApi.listClientUsers(clientId, { pageSize: 500 });
            setAllUsers(res.data);
        } catch {
            // non-blocking
        } finally {
            setIsLoadingUsers(false);
        }
    }, [adminApi, clientId]);

    useEffect(() => {
        fetchOrgs();
    }, [fetchOrgs]);

    // Load users once for user management panels
    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    const handleCreateSaved = useCallback(() => {
        setShowCreateForm(false);
        fetchOrgs();
    }, [fetchOrgs]);

    const handleMembersChanged = useCallback(() => {
        fetchUsers();
        fetchOrgs(); // refresh userCount
    }, [fetchOrgs, fetchUsers]);

    const toggleExpand = useCallback((orgId: number) => {
        setExpandedOrgId((prev) => (prev === orgId ? null : orgId));
    }, []);

    const formatDate = (dateStr: string) => {
        try {
            return new Date(dateStr).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
        } catch {
            return dateStr;
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Building02 className="size-5 text-tertiary" />
                    <h2 className="text-sm font-semibold text-primary">Organizations</h2>
                    {!isLoading && <span className="text-sm text-tertiary">({orgs.length})</span>}
                </div>
                {!showCreateForm && (
                    <Button
                        color="secondary"
                        size="sm"
                        iconLeading={Plus}
                        onClick={() => {
                            setShowCreateForm(true);
                            setExpandedOrgId(null);
                        }}
                    >
                        Add Organization
                    </Button>
                )}
            </div>

            {/* Create form panel */}
            {showCreateForm && (
                <OrgCreatePanel clientId={clientId} typeOptions={typeOptions} onSaved={handleCreateSaved} onCancel={() => setShowCreateForm(false)} />
            )}

            {/* Table */}
            {isLoading ? (
                <div className="flex justify-center py-8">
                    <LoadingIndicator size="sm" label="Loading organizations…" />
                </div>
            ) : error ? (
                <p className="text-sm text-error-primary">{error}</p>
            ) : orgs.length === 0 ? (
                <p className="py-6 text-center text-sm text-quaternary">No organizations yet. Add one above.</p>
            ) : (
                <div className="overflow-hidden rounded-lg border border-secondary">
                    <table className="w-full text-left text-sm">
                        <thead className="border-b border-secondary bg-secondary_subtle">
                            <tr>
                                <th className="px-4 py-3 font-medium text-tertiary">Code</th>
                                <th className="px-4 py-3 font-medium text-tertiary">Name</th>
                                <th className="px-4 py-3 font-medium text-tertiary">Type</th>
                                <th className="px-4 py-3 font-medium text-tertiary">Status</th>
                                <th className="px-4 py-3 font-medium text-tertiary">Members</th>
                                <th className="px-4 py-3 font-medium text-tertiary">Updated</th>
                                <th className="w-8 px-4 py-3"></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-secondary">
                            {orgs.map((org) => {
                                const isExpanded = expandedOrgId === org.id;
                                return (
                                    <React.Fragment key={org.id}>
                                        <tr className="cursor-pointer hover:bg-secondary_subtle" onClick={() => toggleExpand(org.id)}>
                                            <td className="px-4 py-3 font-mono text-xs text-primary">{org.code}</td>
                                            <td className="px-4 py-3 font-medium text-primary">{org.name}</td>
                                            <td className="px-4 py-3">
                                                <Badge color="gray" size="sm" type="pill-color">
                                                    {org.type}
                                                </Badge>
                                            </td>
                                            <td className="px-4 py-3">
                                                <Badge color={org.isActive ? "success" : "gray"} size="sm" type="pill-color">
                                                    {org.isActive ? "Active" : "Inactive"}
                                                </Badge>
                                            </td>
                                            <td className="px-4 py-3 text-tertiary">{org.userCount}</td>
                                            <td className="px-4 py-3 text-tertiary">{formatDate(org.updatedAt)}</td>
                                            <td className="px-4 py-3 text-tertiary">
                                                <ChevronDown className={`size-4 transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                                            </td>
                                        </tr>
                                        {isExpanded && (
                                            <tr key={`${org.id}-expanded`}>
                                                <td colSpan={7} className="p-0">
                                                    <ExpandedOrgPanel
                                                        clientId={clientId}
                                                        org={org}
                                                        typeOptions={typeOptions}
                                                        allUsers={allUsers}
                                                        isLoadingUsers={isLoadingUsers}
                                                        onOrgUpdated={fetchOrgs}
                                                        onCollapse={() => setExpandedOrgId(null)}
                                                        onMembersChanged={handleMembersChanged}
                                                    />
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
